import { useEffect, useState } from 'react';
import { api, type Inbox } from '../api.js';
import { faInt } from '../labels.js';

const EMPTY = {
  slug: '',
  name: '',
  siteUrl: '',
  wooBaseUrl: '',
  wooConsumerKey: '',
  wooConsumerSecret: '',
};

export function Inboxes({ onChanged }: { onChanged: () => void }) {
  const [rows, setRows] = useState<Inbox[]>([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setRows((await api.inboxes()).inboxes);
  }

  useEffect(() => {
    void load();
  }, []);

  /**
   * Turn the widget off on the shop's site, from here.
   *
   * Until now the only way to stop the assistant saying something was a
   * deploy. Switching the inbox off makes bootstrap answer 404 and the widget
   * render nothing at all — no bubble, no panel. Conversations, catalog and
   * customers are untouched, so turning it back on resumes exactly where it
   * stopped.
   */
  const [syncing, setSyncing] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);

  /**
   * Pull the catalog now, and say what moved.
   *
   * Reached for when the automatic path is suspected of having stopped, so an
   * "ok" would be useless — the numbers are the answer to the question that
   * made someone press it.
   */
  async function syncNow(row: Inbox) {
    setSyncing(row.id);
    setReport(null);
    try {
      const { report: r } = await api.syncCatalog(row.id);
      setReport(
        `${faInt(r['fetched'] ?? 0)} محصول خوانده شد · ` +
          `${faInt(r['updated'] ?? 0)} به‌روز · ` +
          `${faInt(r['ranked'] ?? 0)} با رتبه · ` +
          `${faInt(r['gone'] ?? 0)} حذف‌شده از ووکامرس`,
      );
      await load();
    } catch {
      setReport('سینک انجام نشد — ووکامرس پاسخ نداد یا کلیدها معتبر نیستند.');
    } finally {
      setSyncing(null);
    }
  }

  async function toggleActive(row: Inbox) {
    const off = row.isActive;
    if (off && !confirm(`ویجت «${row.name}» روی سایت خاموش شود؟ گفتگوهای ثبت‌شده پاک نمی‌شوند.`)) {
      return;
    }
    await api.updateInbox(row.id, { isActive: !row.isActive });
    await load();
  }

  /**
   * What a handover means for this store.
   *
   * Absent counts as 'chat', matching the server, so a store that never chose
   * reads as what it actually does rather than as blank.
   */
  async function toggleHandoffMode(row: Inbox) {
    const next = row.widgetConfig?.handoffMode === 'callback' ? 'chat' : 'callback';
    if (
      next === 'chat' &&
      !confirm(
        'ارجاع‌ها دوباره به چت زنده برود؟ ' +
          'یعنی گفتگو در پنل منتظر می‌ماند تا کسی جوابش را بدهد.',
      )
    ) {
      return;
    }
    await api.updateInbox(row.id, {
      widgetConfig: { ...(row.widgetConfig ?? {}), handoffMode: next },
    });
    await load();
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createInbox(form);
      setForm({ ...EMPTY });
      setOpen(false);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ساخت اینباکس ناموفق بود');
    } finally {
      setBusy(false);
    }
  }

  const field = (
    key: keyof typeof EMPTY,
    label: string,
    opts: { ltr?: boolean; type?: string; hint?: string } = {},
  ) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <input
        className="input"
        type={opts.type ?? 'text'}
        dir={opts.ltr ? 'ltr' : undefined}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
      {opts.hint && <span className="mt-1 block text-xs text-ink/40">{opts.hint}</span>}
    </label>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">اینباکس‌ها</h2>
          <p className="text-sm text-ink/50">هر اینباکس یک فروشگاه است.</p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(!open)}>
          {open ? 'انصراف' : 'اینباکس جدید'}
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="card space-y-4 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            {field('name', 'نام فروشگاه')}
            {field('slug', 'شناسه', { ltr: true, hint: 'فقط حروف کوچک انگلیسی، عدد و خط تیره' })}
            {field('siteUrl', 'آدرس سایت', { ltr: true })}
            {field('wooBaseUrl', 'آدرس پایه ووکامرس', { ltr: true })}
            {field('wooConsumerKey', 'Consumer Key', { ltr: true, type: 'password' })}
            {field('wooConsumerSecret', 'Consumer Secret', { ltr: true, type: 'password' })}
          </div>
          <p className="rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn-deep">
            کلیدها رمزنگاری‌شده ذخیره می‌شوند و دیگر قابل مشاهده نیستند — فقط چهار
            کاراکتر آخر نمایش داده می‌شود. دسترسی کلید باید فقط-خواندنی باشد.
          </p>
          {error && (
            <p className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad-deep">{error}</p>
          )}
          <button className="btn-primary" disabled={busy}>
            {busy ? 'در حال ساخت…' : 'ساخت اینباکس'}
          </button>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {rows.map((i) => (
          <div key={i.id} className="card p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold">{i.name}</h3>
                <p className="text-xs text-ink/40" dir="ltr">{i.slug}</p>
              </div>
              <button
                onClick={() => void toggleActive(i)}
                title={
                  i.isActive
                    ? 'خاموش کردن ویجت روی سایت — گفتگوهای ثبت‌شده دست‌نخورده می‌مانند'
                    : 'روشن کردن دوباره‌ی ویجت'
                }
                className={`badge transition ${i.isActive ? 'bg-ok-bg text-ok-deep hover:brightness-95' : 'bg-bad-bg text-bad-deep hover:brightness-95'}`}
              >
                {i.isActive ? 'فعال — کلیک برای خاموش کردن' : 'خاموش — کلیک برای روشن کردن'}
              </button>
            </div>
            {/*
              Its own row rather than a badge beside "active", because it is a
              different kind of switch: one turns the widget off, this one
              changes where an answer comes back from.
            */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-ink/[.07] pt-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-ink-strong">وقتی ربات به آدم می‌سپارد</p>
                <p className="text-2xs text-ink/50">
                  {i.widgetConfig?.handoffMode === 'callback'
                    ? 'راه ارتباطی می‌گیرد و کسی بعداً خبر می‌دهد'
                    : 'گفتگو در پنل منتظر می‌ماند تا اپراتور جواب دهد'}
                </p>
              </div>
              <button
                onClick={() => void toggleHandoffMode(i)}
                title="جابه‌جایی بین درخواست تماس و چت زنده"
                className={`badge transition hover:brightness-95 ${
                  i.widgetConfig?.handoffMode === 'callback'
                    ? 'bg-brand-100 text-brand'
                    : 'bg-ink/[0.06] text-ink/70'
                }`}
              >
                {i.widgetConfig?.handoffMode === 'callback' ? 'درخواست تماس' : 'چت زنده'}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink/[.07] pt-3">
              <button
                className="btn-ghost text-xs"
                disabled={syncing === i.id}
                onClick={() => void syncNow(i)}
              >
                {syncing === i.id ? 'در حال سینک…' : 'سینک کاتالوگ'}
              </button>
              <span className="text-xs text-ink/50">
                {/* The whole point of showing this: a webhook that has quietly
                    switched itself off looks exactly like a shop nobody has
                    edited, until you see the date. */}
                {i.lastSyncedAt
                  ? `آخرین سینک موفق: ${new Date(i.lastSyncedAt).toLocaleString('fa-IR')}`
                  : 'تا حالا سینک نشده'}
              </span>
            </div>
            {report && syncing !== i.id && (
              <p className="mt-2 rounded-lg bg-paper p-2 text-xs text-ink/65">{report}</p>
            )}

            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-ink/50">سایت</dt>
                <dd dir="ltr" className="truncate">{i.siteUrl ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ink/50">کلید ووکامرس</dt>
                <dd dir="ltr">
                  {i.hasCredentials ? (
                    <span className="text-ok">•••• {i.wooKeyHint}</span>
                  ) : (
                    <span className="text-bad">تنظیم نشده</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
